import { useEffect, useMemo, useState } from "react"
import { useQuery, ZeroProvider } from "@rocicorp/zero/react"
import { Outlet, useNavigate } from "@tanstack/react-router"

import { Loader, useDarkMode } from "@productlane/ui"
import { queries } from "@productlane/zero/queries"

import type { AccentMode } from "@/lib/accent-color"
import { ImpersonationBar } from "@/components/impersonation/impersonation-bar"
import { LiveChatNotificationWatcher } from "@/components/live-chat-notification-watcher"
import { OfflineModal } from "@/components/offline-modal"
import {
	cacheAccentColor,
	readCachedAccentColor,
	resolveAccentColor,
} from "@/lib/accent-color"
import { api } from "@/lib/api"
import { useZeroConfig } from "@/lib/zero"
import { ProductMapBanner } from "@/screens/app/helpCenter/product-map-banner"
import { SiteImportBanner } from "@/screens/app/helpCenter/site-import-banner"
import { NotificationDesktopWatcher } from "./app/notifications/desktop-notification-watcher"
import { NotificationFaviconBadge } from "./app/notifications/notification-favicon-badge"

const FullscreenLoading = () => {
	return (
		<div className="flex h-dvh flex-col items-center justify-center">
			<Loader size={10} />
		</div>
	)
}

function GlobalStyle() {
	const [workspace] = useQuery(queries.currentWorkspace())
	const { isDarkMode } = useDarkMode()
	const mode: AccentMode = isDarkMode ? "dark" : "light"
	const loaded = isDarkMode
		? workspace?.darkModeAccentColor
		: workspace?.accentColor
	const cached = useMemo(() => readCachedAccentColor(mode), [mode])

	useEffect(() => {
		if (loaded) cacheAccentColor(mode, loaded)
	}, [loaded, mode])

	return (
		// eslint-disable-next-line react/no-unknown-property
		<style jsx global>{`
			html {
				--portal-accent-color: ${resolveAccentColor({ mode, loaded, cached })};
			}
		`}</style>
	)
}

const kvStore = "idb"

export function ZeroLoader() {
	const [, setIsInitialized] = useState(false)
	const zeroConfig = useZeroConfig({ setIsInitialized })
	const navigate = useNavigate()
	const userQuery = api.user.info.useQuery(undefined, {})

	useEffect(() => {
		if (userQuery.data === null) {
			void navigate({ to: "/logout", replace: true })
		}
	}, [navigate, userQuery.data])

	const content = (
		<>
			{zeroConfig ? (
				<ZeroProvider {...zeroConfig} kvStore={kvStore}>
					<GlobalStyle />
					<OfflineModal />
					<LiveChatNotificationWatcher />
					<NotificationDesktopWatcher />
					<NotificationFaviconBadge />
					<Outlet />
				</ZeroProvider>
			) : (
				<FullscreenLoading />
			)}
		</>
	)

	return (
		<>
			<ImpersonationBar />
			<div className="pointer-events-none fixed inset-x-0 bottom-4 z-50 flex flex-col items-center gap-2 px-4">
				<SiteImportBanner />
				<ProductMapBanner />
			</div>
			{content}
		</>
	)
}
